/**
 * AddMediaActionDialog - Dialog para adicionar/editar ação de mídia
 * 
 * Suporta: Imagem, Vídeo, Áudio, Documento
 * Permite selecionar da galeria ou inserir URL externa
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link, FolderOpen, Image, Video, Music, FileText } from 'lucide-react';
import { MediaGalleryPicker } from '@/components/tenant/media/MediaGalleryPicker';
import { RoutingActionType } from '@/pages/tenant/RoutingRules';
import { actionTypeConfig, RoutingMediaType, actionToMediaType } from '../types';
import { MediaFileType } from '@/types/media';

// =====================================================
// INTERFACES
// =====================================================

interface MediaActionData {
  media_url: string;
  filename?: string;
  caption?: string;
}

interface AddMediaActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: MediaActionData) => void;
  actionType: RoutingActionType;
  initialValue?: Record<string, unknown>;
}

// Mapeamento de RoutingActionType para MediaFileType da galeria
const actionToFileType: Record<RoutingMediaType, MediaFileType[]> = {
  image: ['image'],
  video: ['video'],
  audio: ['audio'],
  document: ['document'],
};

// =====================================================
// COMPONENTE
// =====================================================

export function AddMediaActionDialog({
  open,
  onOpenChange,
  onSave,
  actionType,
  initialValue,
}: AddMediaActionDialogProps) {
  // Estado do formulário
  const [sourceType, setSourceType] = useState<'url' | 'gallery'>('gallery');
  const [mediaUrl, setMediaUrl] = useState('');
  const [filename, setFilename] = useState('');
  const [caption, setCaption] = useState('');
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);

  // Configuração do tipo atual
  const config = actionTypeConfig[actionType];
  const mediaType = actionToMediaType[actionType];
  const allowedFileTypes = mediaType ? actionToFileType[mediaType] : [];
  const supportsCaption = actionType !== 'send_audio';

  // Reset ao abrir dialog
  useEffect(() => {
    if (open) {
      if (initialValue) {
        setMediaUrl((initialValue.media_url as string) || '');
        setFilename((initialValue.filename as string) || '');
        setCaption((initialValue.caption as string) || '');
        setSourceType(
          (initialValue.media_url as string)?.startsWith('http') ? 'url' : 'gallery'
        );
      } else {
        setMediaUrl('');
        setFilename('');
        setCaption('');
        setSourceType('gallery');
      }
      setShowGalleryPicker(false);
    }
  }, [open, initialValue]);

  // Handler de salvar
  const handleSave = () => {
    if (mediaUrl.trim()) {
      onSave({
        media_url: mediaUrl.trim(),
        filename: filename || undefined,
        caption: caption.trim() || undefined,
      });
    }
  };

  // Handler de seleção da galeria
  const handleGallerySelect = (media: any) => {
    setMediaUrl(media.storage_path);
    setFilename(media.name);
    setShowGalleryPicker(false);
  };

  // Validação
  const isValid = mediaUrl.trim().length > 0;

  // Ícone baseado no tipo
  const getIcon = () => {
    switch (actionType) {
      case 'send_image': return <Image className="h-4 w-4" />;
      case 'send_video': return <Video className="h-4 w-4" />;
      case 'send_audio': return <Music className="h-4 w-4" />;
      case 'send_document': return <FileText className="h-4 w-4" />;
      default: return null;
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getIcon()}
            {initialValue ? `Editar ${config.label}` : `Adicionar ${config.label}`}
          </DialogTitle>
          <DialogDescription>
            Selecione um arquivo da galeria ou insira uma URL externa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Tabs para escolher fonte */}
          <Tabs value={sourceType} onValueChange={(v) => setSourceType(v as 'url' | 'gallery')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="gallery" className="gap-2">
                <FolderOpen className="h-4 w-4" />
                Galeria de Mídia
              </TabsTrigger>
              <TabsTrigger value="url" className="gap-2">
                <Link className="h-4 w-4" />
                URL Externa
              </TabsTrigger>
            </TabsList>

            {/* Galeria */}
            <TabsContent value="gallery" className="space-y-4 mt-4">
              {showGalleryPicker ? (
                <MediaGalleryPicker
                  allowedTypes={allowedFileTypes}
                  onSelect={handleGallerySelect}
                  onCancel={() => setShowGalleryPicker(false)}
                  currentSelection={undefined}
                />
              ) : (
                <div className="space-y-4">
                  {mediaUrl && !mediaUrl.startsWith('http') ? (
                    <div className="p-4 border rounded-lg bg-muted/50">
                      <p className="text-sm font-medium mb-1">Arquivo selecionado:</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {filename || mediaUrl}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => setShowGalleryPicker(true)}
                      >
                        Trocar arquivo
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full h-24"
                      onClick={() => setShowGalleryPicker(true)}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <FolderOpen className="h-8 w-8 text-muted-foreground" />
                        <span>Selecionar da Galeria</span>
                      </div>
                    </Button>
                  )}
                </div>
              )}
            </TabsContent>

            {/* URL Externa */}
            <TabsContent value="url" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="media-url">URL do Arquivo *</Label>
                <Input
                  id="media-url"
                  value={sourceType === 'url' ? mediaUrl : ''}
                  onChange={(e) => {
                    setMediaUrl(e.target.value);
                    setFilename('');
                  }}
                  placeholder="https://exemplo.com/arquivo.jpg"
                />
                <p className="text-xs text-muted-foreground">
                  Cole a URL pública do arquivo que deseja enviar.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          {/* Campo de nome do arquivo (para documento) */}
          {actionType === 'send_document' && !showGalleryPicker && (
            <div className="space-y-2">
              <Label htmlFor="filename">Nome do Arquivo (opcional)</Label>
              <Input
                id="filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="documento.pdf"
              />
            </div>
          )}

          {/* Campo de legenda (quando permitido) - oculto durante seleção na galeria */}
          {supportsCaption && !showGalleryPicker && (
            <div className="space-y-2">
              <Label htmlFor="caption">Legenda (opcional)</Label>
              <Textarea
                id="caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Digite uma legenda para a mídia..."
                rows={2}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Variáveis: <code className="bg-muted px-1 rounded">{'{{contact_name}}'}</code> (nome completo),{' '}
                <code className="bg-muted px-1 rounded">{'{{contact_first_name}}'}</code> (apenas o primeiro nome).
              </p>

            </div>
          )}
        </div>

        {/* Footer oculto durante seleção na galeria (picker tem botões próprios) */}
        {!showGalleryPicker && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!isValid}>
              {initialValue ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
